import { useState } from "react";
import { toast } from "sonner";
import { Plus, Warning, Check, PencilSimple, X, FloppyDisk } from "@phosphor-icons/react";
import api, { formatApiError } from "../../lib/api";
import { fmt$, inputCls, cellInputCls } from "./shared";

const CATEGORIES = ["Hardware", "Software", "Components", "Consumables", "Raw Materials"];
const blank = () => ({ sku: "", name: "", category: "Hardware", unit_price: "", stock: 0, reorder_point: 10 });

export function Products({ products, onChange }) {
  const [form, setForm] = useState(blank());
  const [editing, setEditing] = useState(null);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const create = async (e) => {
    e.preventDefault();
    try {
      await api.post("/scm/products", { ...form, unit_price: Number(form.unit_price), stock: Number(form.stock), reorder_point: Number(form.reorder_point) });
      toast.success("Product added");
      setForm(blank());
      onChange();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const save = async () => {
    try {
      await api.patch(`/scm/products/${editing.id}`, {
        sku: editing.sku, name: editing.name, category: editing.category,
        unit_price: Number(editing.unit_price), reorder_point: Number(editing.reorder_point),
      });
      toast.success("Product updated");
      setEditing(null);
      onChange();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  return (
    <div>
      <form onSubmit={create} className="grid grid-cols-2 md:grid-cols-[100px_1fr_140px_120px_100px_100px_auto] gap-px bg-zinc-200 border-b border-zinc-950" data-testid="product-form">
        <input value={form.sku} onChange={(e) => set("sku", e.target.value)} required placeholder="SKU" data-testid="product-sku-input" className={`${inputCls} tnum`} />
        <input value={form.name} onChange={(e) => set("name", e.target.value)} required placeholder="Name" data-testid="product-name-input" className={inputCls} />
        <select value={form.category} onChange={(e) => set("category", e.target.value)} data-testid="product-category-select" className={inputCls}>
          {CATEGORIES.map((c) => <option key={c}>{c}</option>)}
        </select>
        <input type="number" step="0.01" min="0" value={form.unit_price} onChange={(e) => set("unit_price", e.target.value)} required placeholder="Price" data-testid="product-price-input" className={`${inputCls} tnum`} />
        <input type="number" min="0" value={form.stock} onChange={(e) => set("stock", e.target.value)} placeholder="Stock" data-testid="product-stock-input" className={`${inputCls} tnum`} />
        <input type="number" min="0" value={form.reorder_point} onChange={(e) => set("reorder_point", e.target.value)} placeholder="Reorder" data-testid="product-reorder-input" className={`${inputCls} tnum`} />
        <button type="submit" className="bg-zinc-950 text-white text-xs font-semibold px-5 hover:bg-[#FF3E00] flex items-center justify-center gap-1" data-testid="product-create-btn">
          <Plus size={14} /> ADD
        </button>
      </form>
      <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead className="bg-zinc-50 border-b border-zinc-950">
          <tr className="text-xs tracking-wider uppercase">
            <th className="text-left px-4 py-2.5">SKU</th>
            <th className="text-left px-4 py-2.5">Product</th>
            <th className="text-left px-4 py-2.5">Category</th>
            <th className="text-right px-4 py-2.5">Unit Price</th>
            <th className="text-right px-4 py-2.5">Avg Cost</th>
            <th className="text-right px-4 py-2.5">Stock</th>
            <th className="text-right px-4 py-2.5">Reorder At</th>
            <th className="w-40" />
          </tr>
        </thead>
        <tbody data-testid="products-tbody">
          {products.map((p) => (editing?.id === p.id
            ? <EditRow key={p.id} row={editing} setRow={setEditing} onSave={save} onCancel={() => setEditing(null)} />
            : <ViewRow key={p.id} p={p} onEdit={() => setEditing({ ...p })} />
          ))}
        </tbody>
      </table>
      </div>
    </div>
  );
}

function ViewRow({ p, onEdit }) {
  const low = p.stock <= p.reorder_point;
  return (
    <tr className="border-b border-zinc-100 hover:bg-zinc-50" data-testid={`product-row-${p.sku}`}>
      <td className="px-4 py-2.5 tnum text-xs">{p.sku}</td>
      <td className="px-4 py-2.5 font-medium">{p.name}</td>
      <td className="px-4 py-2.5 text-zinc-500">{p.category}</td>
      <td className="px-4 py-2.5 text-right tnum">{fmt$(p.unit_price)}</td>
      <td className="px-4 py-2.5 text-right tnum text-zinc-500">{fmt$(p.avg_cost)}</td>
      <td className="px-4 py-2.5 text-right tnum font-semibold" data-testid={`product-stock-${p.sku}`}>{p.stock}</td>
      <td className="px-4 py-2.5 text-right tnum text-zinc-500">{p.reorder_point}</td>
      <td className="px-4 py-2.5">
        <div className="flex items-center gap-3">
          {low ? <span className="text-[10px] px-1.5 py-0.5 bg-red-50 text-[#D0021B] flex items-center gap-1 w-fit"><Warning size={10} /> LOW</span> : <span className="text-[10px] px-1.5 py-0.5 bg-emerald-50 text-emerald-700 flex items-center gap-1 w-fit"><Check size={10} /> OK</span>}
          <button onClick={onEdit} className="text-[10px] font-mono text-zinc-500 hover:text-zinc-950 flex items-center gap-1" data-testid={`product-edit-${p.sku}`}><PencilSimple size={11} /> EDIT</button>
        </div>
      </td>
    </tr>
  );
}

function EditRow({ row, setRow, onSave, onCancel }) {
  const set = (k, v) => setRow((r) => ({ ...r, [k]: v }));
  return (
    <tr className="border-b border-zinc-100 bg-amber-50/40" data-testid="product-edit-row">
      <td className="px-2 py-1.5"><input value={row.sku} onChange={(e) => set("sku", e.target.value)} className={`${cellInputCls} tnum`} data-testid="product-edit-sku" /></td>
      <td className="px-2 py-1.5"><input value={row.name} onChange={(e) => set("name", e.target.value)} className={cellInputCls} data-testid="product-edit-name" /></td>
      <td className="px-2 py-1.5">
        <select value={row.category} onChange={(e) => set("category", e.target.value)} className={cellInputCls} data-testid="product-edit-category">
          {[...new Set([...CATEGORIES, row.category])].map((c) => <option key={c}>{c}</option>)}
        </select>
      </td>
      <td className="px-2 py-1.5"><input type="number" step="0.01" min="0" value={row.unit_price} onChange={(e) => set("unit_price", e.target.value)} className={`${cellInputCls} text-right tnum`} data-testid="product-edit-price" /></td>
      <td className="px-4 py-1.5 text-right tnum text-zinc-400 text-xs">{fmt$(row.avg_cost)}</td>
      <td className="px-4 py-1.5 text-right tnum text-zinc-400 text-xs">{row.stock}</td>
      <td className="px-2 py-1.5"><input type="number" min="0" value={row.reorder_point} onChange={(e) => set("reorder_point", e.target.value)} className={`${cellInputCls} text-right tnum`} data-testid="product-edit-reorder" /></td>
      <td className="px-2 py-1.5">
        <div className="flex items-center gap-2">
          <button onClick={onSave} className="bg-zinc-950 text-white text-[10px] font-semibold px-3 py-1.5 hover:bg-[#FF3E00] flex items-center gap-1" data-testid="product-save-btn"><FloppyDisk size={11} /> SAVE</button>
          <button onClick={onCancel} className="text-[10px] font-mono text-zinc-500 hover:text-zinc-950 flex items-center gap-1" data-testid="product-cancel-edit-btn"><X size={11} /></button>
        </div>
      </td>
    </tr>
  );
}
